import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import ManagementModalLayout from '../ManagementModalLayout/ManagementModalLayout';
import ConfirmActionModal from '../ConfirmActionModal/ConfirmActionModal';
import './ManageProductsModal.css';
import {
  createProduct,
  deleteProduct,
  fetchProducts,
  type ManagedProduct,
  updateProduct,
} from '../../auth/productManagement';
import {
  fetchProductCategories,
  type ManagedProductCategory,
} from '../../auth/productCategoryManagement';
import { useI18n } from '../../i18n/I18nContext';

interface ManageProductsModalProps {
  onClose: () => void;
  onCatalogUpdated: () => Promise<void>;
  showToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

interface ProductDraft {
  originalId?: string;
  id?: string;
  name: string;
  price: string;
  categoryId: number | null;
  isActive: boolean;
}

interface ProductCategoryGroup {
  key: string;
  label: string;
  categoryIsActive: boolean;
  products: ManagedProduct[];
}

const UNCATEGORIZED_CATEGORY_KEY = 'uncategorized';
const productSort = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

const emptyDraft = (defaultCategoryId: number | null = null): ProductDraft => ({
  id: '',
  name: '',
  price: '',
  categoryId: defaultCategoryId,
  isActive: true,
});

function getDefaultCategoryId(categories: ManagedProductCategory[]) {
  const generalCategory = categories.find(
    (category) => category.isActive && category.name.trim().toLowerCase() === 'general',
  );

  if (generalCategory) {
    return generalCategory.id;
  }

  return categories.find((category) => category.isActive)?.id ?? null;
}

function getCategoryGroupKey(categoryId?: number | null) {
  return categoryId == null ? UNCATEGORIZED_CATEGORY_KEY : `category-${categoryId}`;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getCategoryInternalId(
  categories: ManagedProductCategory[],
  categoryId: number | null,
) {
  if (categoryId == null) {
    return null;
  }

  const internalId = categories.find((category) => category.id === categoryId)?.internalId?.trim().toLowerCase();
  return internalId ? internalId : null;
}

function getSuggestedProductId(
  categories: ManagedProductCategory[],
  products: ManagedProduct[],
  categoryId: number | null,
) {
  const internalId = getCategoryInternalId(categories, categoryId);

  if (!internalId) {
    return '';
  }

  const matcher = new RegExp(`^${escapeRegExp(internalId)}_(\\d+)$`, 'i');
  const maxSuffix = products.reduce((highest, product) => {
    if (product.categoryId !== categoryId) {
      return highest;
    }

    const match = product.id.match(matcher);

    if (!match) {
      return highest;
    }

    const numericSuffix = Number.parseInt(match[1], 10);
    return Number.isFinite(numericSuffix) ? Math.max(highest, numericSuffix) : highest;
  }, 0);

  return `${internalId}_${maxSuffix + 1}`;
}

function buildCreateProductDraft(
  categories: ManagedProductCategory[],
  products: ManagedProduct[],
  preferredCategoryId?: number | null,
): ProductDraft {
  const categoryId = preferredCategoryId ?? getDefaultCategoryId(categories);

  return {
    ...emptyDraft(categoryId),
    id: getSuggestedProductId(categories, products, categoryId),
  };
}

const ManageProductsModal: React.FC<ManageProductsModalProps> = ({ onClose, onCatalogUpdated, showToast }) => {
  const { t } = useI18n();
  const productIdInputRef = useRef<HTMLInputElement | null>(null);
  const [products, setProducts] = useState<ManagedProduct[]>([]);
  const [categories, setCategories] = useState<ManagedProductCategory[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ProductDraft>(emptyDraft());
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const groupedProducts = useMemo<ProductCategoryGroup[]>(() => {
    const groups = new Map<string, ProductCategoryGroup>();
    const categoryActivityById = new Map(
      categories.map((category) => [category.id, category.isActive] as const),
    );

    for (const product of products) {
      const key = getCategoryGroupKey(product.categoryId);
      const label = product.categoryName?.trim() || t('products.noCategory');
      const categoryIsActive = product.categoryId == null
        ? true
        : (categoryActivityById.get(product.categoryId) ?? product.categoryIsActive ?? true);
      const existing = groups.get(key);

      if (existing) {
        existing.products.push(product);
        continue;
      }

      groups.set(key, {
        key,
        label,
        categoryIsActive,
        products: [product],
      });
    }

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        products: [...group.products].sort((left, right) => {
          const byName = productSort.compare(left.name, right.name);
          return byName !== 0 ? byName : productSort.compare(left.id, right.id);
        }),
      }))
      .sort((left, right) => {
        if (left.key === UNCATEGORIZED_CATEGORY_KEY) {
          return 1;
        }
        if (right.key === UNCATEGORIZED_CATEGORY_KEY) {
          return -1;
        }
        return productSort.compare(left.label, right.label);
      });
  }, [categories, products, t]);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const [nextProducts, nextCategories] = await Promise.all([
          fetchProducts(),
          fetchProductCategories(),
        ]);

        if (cancelled) {
          return;
        }

        setProducts(nextProducts);
        setCategories(nextCategories);
        setSelectedProductId(null);
        setDraft(buildCreateProductDraft(nextCategories, nextProducts));
      } catch (nextError) {
        if (!cancelled) {
          setError(nextError instanceof Error ? nextError.message : t('products.loadError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadData();

    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    setExpandedCategories((current) => {
      let changed = false;
      const next = { ...current };

      for (const group of groupedProducts) {
        if (!(group.key in next)) {
          next[group.key] = false;
          changed = true;
        }
      }

      for (const key of Object.keys(next)) {
        if (!groupedProducts.some((group) => group.key === key)) {
          delete next[key];
          changed = true;
        }
      }

      return changed ? next : current;
    });
  }, [groupedProducts]);

  useEffect(() => {
    if (selectedProductId !== null) {
      return;
    }

    const timer = window.setTimeout(() => {
      productIdInputRef.current?.focus();
      productIdInputRef.current?.select();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [selectedProductId, categories.length]);

  function selectProduct(product: ManagedProduct, availableCategories: ManagedProductCategory[] = categories) {
    const fallbackCategoryId = getDefaultCategoryId(availableCategories);
    setSelectedProductId(product.id);
    setDraft({
      originalId: product.id,
      id: product.id,
      name: product.name,
      price: product.price == null ? '' : String(product.price),
      categoryId: product.categoryId ?? fallbackCategoryId,
      isActive: product.isActive,
    });
    setError(null);
  }

  function startCreateProduct() {
    setSelectedProductId(null);
    setDraft(buildCreateProductDraft(categories, products));
    setError(null);
  }

  function toggleCategory(key: string) {
    setExpandedCategories((current) => ({
      ...current,
      [key]: !current[key],
    }));
  }

  const isEditingExistingProduct = Boolean(draft.originalId);

  async function reloadProducts(
    preferredId?: string | null,
    categoryIdToExpand?: number | null,
    createDraftOverrides?: Partial<ProductDraft> | null,
  ) {
    const [nextProducts, nextCategories] = await Promise.all([
      fetchProducts(),
      fetchProductCategories(),
    ]);
    setProducts(nextProducts);
    setCategories(nextCategories);

    if (categoryIdToExpand !== undefined) {
      const key = getCategoryGroupKey(categoryIdToExpand);
      setExpandedCategories((current) => ({
        ...current,
        [key]: true,
      }));
    }

    if (createDraftOverrides) {
      setSelectedProductId(null);
      const nextCreateDraft = buildCreateProductDraft(
        nextCategories,
        nextProducts,
        createDraftOverrides.categoryId,
      );
      setDraft({
        ...nextCreateDraft,
        ...createDraftOverrides,
      });
    } else {
      const nextSelected =
        (preferredId ? nextProducts.find((product) => product.id === preferredId) : undefined) ??
        nextProducts[0];

      if (nextSelected) {
        selectProduct(nextSelected, nextCategories);
      } else {
        setSelectedProductId(null);
        setDraft(buildCreateProductDraft(nextCategories, nextProducts));
      }
    }

    await onCatalogUpdated();
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      if (!draft.id?.trim()) {
        throw new Error(t('products.requireId'));
      }
      if (!draft.name.trim()) {
        throw new Error(t('products.requireName'));
      }
      if (draft.categoryId == null) {
        throw new Error(t('products.requireCategory'));
      }
      const normalizedPrice = draft.price.trim() ? Number(draft.price) : null;
      if (normalizedPrice != null && (!Number.isFinite(normalizedPrice) || normalizedPrice < 0)) {
        throw new Error(t('products.requireValidValue'));
      }

      const savedProduct = draft.originalId
        ? await updateProduct({
          currentId: draft.originalId,
          id: draft.id.trim(),
          name: draft.name,
          price: normalizedPrice,
          categoryId: draft.categoryId,
          isActive: draft.isActive,
        })
        : await createProduct({
          id: draft.id.trim(),
          name: draft.name,
          price: normalizedPrice,
          categoryId: draft.categoryId,
          isActive: draft.isActive,
        });

      if (draft.originalId) {
        await reloadProducts(savedProduct.id, savedProduct.categoryId ?? null);
      } else {
        await reloadProducts(null, savedProduct.categoryId ?? null, {
          categoryId: savedProduct.categoryId ?? getDefaultCategoryId(categories),
        });
      }
      showToast(draft.originalId ? t('products.updated') : t('products.created'), 'success');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : t('products.saveError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!draft.originalId) {
      startCreateProduct();
      return;
    }

    setDeleting(true);
    setError(null);
    try {
      await deleteProduct(draft.originalId);
      setConfirmingDelete(false);
      await reloadProducts(selectedProductId === draft.originalId ? null : selectedProductId);
      showToast(t('products.deleted'), 'success');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : t('products.deleteError'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <ManagementModalLayout
        ariaLabel={t('products.title')}
        title={t('products.title')}
        closeAriaLabel={t('products.close')}
        onClose={onClose}
        className="manage-products-modal"
        addButtonLabel={t('products.addProduct')}
        addButtonAriaLabel={t('products.addProductAria')}
        onAdd={startCreateProduct}
        sidebar={
          <>
            {loading ? (
              <div className="manage-products-loading">{t('products.loading')}</div>
            ) : products.length === 0 ? (
              <div className="manage-products-empty">{t('products.empty')}</div>
            ) : (
              <div className="management-modal-sidebar-scroll manage-products-list">
                {groupedProducts.map((group) => {
                  const expanded = expandedCategories[group.key] === true;

                  return (
                    <section
                      key={group.key}
                      className={[
                        'manage-products-group',
                        group.categoryIsActive ? '' : 'manage-products-group--category-inactive',
                      ].filter(Boolean).join(' ')}
                    >
                      <button
                        type="button"
                        className="manage-products-group-toggle"
                        onClick={() => toggleCategory(group.key)}
                        aria-expanded={expanded}
                      >
                        <span className="manage-products-group-title">
                          <span>{group.label}</span>
                          <span className="manage-products-group-count">{group.products.length}</span>
                          {!group.categoryIsActive ? (
                            <span className="manage-products-group-badge">
                              {t('products.inactive')}
                            </span>
                          ) : null}
                        </span>
                        <span className="manage-products-group-summary">
                          <span className="manage-products-group-chevron">
                            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                          </span>
                        </span>
                      </button>
                      {expanded ? (
                        <div className="manage-products-group-items">
                          {group.products.map((product) => (
                            <button
                              key={product.id}
                              type="button"
                              className={[
                                'manage-products-item',
                                selectedProductId === product.id ? 'active' : '',
                                group.categoryIsActive ? '' : 'is-inactive-category',
                                product.isActive ? '' : 'is-inactive',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              onClick={() => selectProduct(product)}
                            >
                              <strong>{product.name}</strong>
                              <small>
                                {product.id}
                                {product.price != null ? ` | ${t('products.valueShort', { value: product.price.toFixed(2) })}` : ''}
                                {' | '}
                                {product.isActive ? t('products.active') : t('products.inactive')}
                              </small>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </section>
                  );
                })}
              </div>
            )}
          </>
        }
        form={
          <div className="manage-products-form">
            <div className="management-modal-form-header manage-products-form-header">
              <h4>{draft.originalId ? t('products.editProduct') : t('products.createProduct')}</h4>
              <label className="management-modal-toggle manage-products-active">
                <span>{t('products.active')}</span>
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(event) => setDraft((current) => ({ ...current, isActive: event.target.checked }))}
                />
              </label>
            </div>

            <div className="manage-products-mode">
              <div className={`manage-products-mode-badge ${isEditingExistingProduct ? 'is-editing' : 'is-creating'}`}>
                {isEditingExistingProduct ? t('products.editingModeTitle') : t('products.creatingModeTitle')}
              </div>
              {isEditingExistingProduct ? (
                <button type="button" className="manage-products-inline-action" onClick={startCreateProduct}>
                  {t('products.switchToCreate')}
                </button>
              ) : null}
            </div>

            {error && <div className="manage-products-error">{error}</div>}

            <div className="manage-products-identity-row">
              <div className="manage-products-field">
                <label>{t('products.id')}</label>
                <input
                  ref={productIdInputRef}
                  type="text"
                  value={draft.id ?? ''}
                  onChange={(event) => setDraft((current) => ({ ...current, id: event.target.value }))}
                />
                {isEditingExistingProduct ? (
                  <div className="manage-products-field-hint">{t('products.editingHint')}</div>
                ) : null}
              </div>

              <div className="manage-products-field">
                <label>{t('products.category')}</label>
                <select
                  value={draft.categoryId == null ? '' : String(draft.categoryId)}
                  onChange={(event) => {
                    const nextCategoryId = event.target.value ? Number(event.target.value) : null;

                    setDraft((current) => ({
                      ...current,
                      categoryId: nextCategoryId,
                      ...(current.originalId
                        ? {}
                        : { id: getSuggestedProductId(categories, products, nextCategoryId) }),
                    }));
                  }}
                >
                  {categories.length === 0 ? (
                    <option value="" disabled>
                      {t('products.noCategoriesAvailable')}
                    </option>
                  ) : null}
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                      {category.isActive ? '' : ` (${t('products.inactive')})`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="manage-products-field">
              <label>{t('products.name')}</label>
              <input
                type="text"
                value={draft.name}
                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
              />
            </div>

            <div className="manage-products-field manage-products-field--value">
              <label>{t('products.value')}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={draft.price}
                onChange={(event) => setDraft((current) => ({ ...current, price: event.target.value }))}
                placeholder={t('products.valuePlaceholder')}
              />
            </div>

            <div className="manage-products-actions">
              <button type="button" className="secondary" onClick={onClose}>
                {t('products.close')}
              </button>
              {draft.originalId ? (
                <button
                  type="button"
                  className="danger"
                  onClick={() => setConfirmingDelete(true)}
                  disabled={deleting || saving}
                >
                  {deleting ? t('products.deleting') : t('products.deleteButton')}
                </button>
              ) : null}
              <button type="button" className="primary" onClick={handleSave} disabled={saving || deleting}>
                {saving ? t('products.saving') : draft.originalId ? t('products.saveChanges') : t('products.createButton')}
              </button>
            </div>
          </div>
        }
      />
      {confirmingDelete && draft.originalId ? (
        <ConfirmActionModal
          title={t('confirm.deleteTitle')}
          message={t('confirm.deleteProductBody', { name: draft.name || draft.id || '' })}
          confirmLabel={deleting ? t('products.deleting') : t('confirm.delete')}
          cancelLabel={t('confirm.cancel')}
          onConfirm={() => void handleDelete()}
          onCancel={() => {
            if (!deleting) {
              setConfirmingDelete(false);
            }
          }}
          loading={deleting}
        />
      ) : null}
    </>
  );
};

export default ManageProductsModal;
